import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useFaqs, localizeFaq } from "@/hooks/useFaqs";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import FaqSchema from "@/components/seo/FaqSchema";

interface FAQ {
  question: string;
  answer: string;
}

interface Props {
  faqs: FAQ[];
  color?: string;
  slug?: string;
}

const IndustryFAQ = ({ faqs: hardcodedFaqs, color, slug }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const { data: dbFaqs = [] } = useFaqs("industry", slug ?? "");

  const rawFaqs = dbFaqs.length > 0 ? dbFaqs : hardcodedFaqs;
  const faqs = rawFaqs.map((f) => localizeFaq(f as any, lang));

  if (!faqs || faqs.length === 0) return null;

  return (
    <section id="faq" className="py-14 sm:py-20 scroll-mt-20">
      <FaqSchema items={faqs} />
      <div className="container mx-auto px-6 lg:px-10 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
        <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full bg-primary" />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {({ en: "Industry Guidance", es: "Orientación sectorial", "zh-cn": "行业实务解答" }[lang])}
            </p>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">
            {t("faqTitle")}
          </h2>
        </motion.div>

        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04, duration: 0.3 }}
            >
              <AccordionItem value={`faq-${i}`} className="border-border/50">
                <AccordionTrigger className="text-sm font-medium text-foreground text-left hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default IndustryFAQ;
